import { useState, useCallback } from 'react';
import { contactTypeService } from '../services/contactTypeService';
import { useAccessToken } from './useAuth';
import { useAlert } from '../context/AlertContext';

/**
 * Custom hook for managing contact types
 * @returns {Object} Contact types state and methods
 */
export const useContactTypes = () => {
    const [contactTypes, setContactTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();

    /**
     * Load all contact types
     */
    const loadContactTypes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getBackendAccessToken();
            const data = await contactTypeService.getAll(token);
            setContactTypes(data);
            return data;
        } catch (err) {
            const errorMessage = err.message || 'Failed to load contact types';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error loading contact types:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    /**
     * Get a single contact type by ID
     * @param {number} id - Contact type ID
     */
    const getContactType = useCallback(async (id) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getBackendAccessToken();
            const data = await contactTypeService.getById(token, id);
            return data;
        } catch (err) {
            const errorMessage = err.message || 'Failed to load contact type';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error loading contact type:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    /**
     * Create a new contact type
     * @param {Object} contactTypeData - Contact type data
     */
    const createContactType = useCallback(async (contactTypeData) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getBackendAccessToken();
            const newContactType = await contactTypeService.create(token, contactTypeData);
            setInfoAlert('Contact type created successfully');
            await loadContactTypes(); // Refresh list
            return newContactType;
        } catch (err) {
            const errorMessage = err.message || 'Failed to create contact type';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error creating contact type:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setInfoAlert, setErrorAlert, loadContactTypes]);

    /**
     * Update an existing contact type
     * @param {number} id - Contact type ID
     * @param {Object} contactTypeData - Updated contact type data
     */
    const updateContactType = useCallback(async (id, contactTypeData) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getBackendAccessToken();
            const updatedContactType = await contactTypeService.update(token, id, contactTypeData);
            setInfoAlert('Contact type updated successfully');
            await loadContactTypes(); // Refresh list
            return updatedContactType;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update contact type';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error updating contact type:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setInfoAlert, setErrorAlert, loadContactTypes]);

    /**
     * Delete a contact type
     * @param {number} id - Contact type ID
     */
    const deleteContactType = useCallback(async (id) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getBackendAccessToken();
            await contactTypeService.delete(token, id);
            setInfoAlert('Contact type deleted successfully');
            await loadContactTypes(); // Refresh list
        } catch (err) {
            const errorMessage = err.message || 'Failed to delete contact type';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error deleting contact type:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setInfoAlert, setErrorAlert, loadContactTypes]);

    return {
        contactTypes,
        loading,
        error,
        loadContactTypes,
        getContactType,
        createContactType,
        updateContactType,
        deleteContactType
    };
};

export default useContactTypes;
